import { useMutation, useQuery } from '@tanstack/react-query';
import { CREDIT_PACKS, PLANS } from '@telestore/contracts';
import { formatMoney, formatPhnomPenh } from '@telestore/shared';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { Loader2 } from 'lucide-react';
import { QrCode } from '../components/qr.js';
import {
  Button,
  Empty,
  ErrorBox,
  Panel,
  Pill,
  Row,
  Rows,
  Spinner,
  Stat,
} from '../components/ui.js';

/**
 * Billing (CLAUDE.md §3 `billing`).
 *
 * Two rules shape this screen: the balance is always visible, and the credit
 * ledger is shown in full — a merchant who cannot see where their credits went
 * stops trusting the meter, and a meter nobody trusts cannot be charged for.
 *
 * There is no card form. Cambodia has no practical card autopay, so paying is
 * a KHQR from the platform bot, reconciled through our own payment ladder.
 */
export function BillingPage() {
  const { locale, t } = useSession();

  /**
   * Deliberately not keyed on the shop: this screen shows the ACCOUNT, so it
   * must not change when the merchant switches shops in the header — and it
   * must not be re-fetched per shop either. The plan comes from here too,
   * rather than from the shop, because a shop no longer has one.
   */
  const billing = useQuery({ queryKey: ['account-billing'], queryFn: () => api.billing() });

  /**
   * What the merchant already owes.
   *
   * Polled, because the thing that settles it happens outside this browser —
   * a transfer in a bank app, matched by a bot. Without the poll the merchant
   * pays and then stares at an unchanged screen wondering whether it worked.
   */
  const charges = useQuery({
    queryKey: ['account-charges'],
    queryFn: () => api.openCharges(),
    refetchInterval: 15_000,
  });

  const start = useMutation({
    mutationFn: (plan: 'starter' | 'pro' | 'business') => api.startPlanCharge(plan),
    onSuccess: () => {
      void charges.refetch();
      void billing.refetch();
    },
  });

  if (billing.isPending) return <Spinner label={t('common.loading')} />;
  if (billing.isError || !billing.data) {
    return <ErrorBox message={(billing.error as Error | undefined)?.message ?? t('errors.internal')} />;
  }

  const { balance, subscription, ledger, shops } = billing.data;
  const currentPlan = (subscription?.plan ?? 'free') as keyof typeof PLANS;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label={t('admin.credits')} value={balance.available} tone="brand" />
        <Stat label="Plan" value={balance.plan} />
        <Stat label="Purchased" value={balance.purchased} />
        <Stat
          label={t('admin.shops')}
          value={`${shops.length}/${PLANS[currentPlan].maxShops}`}
        />
        <Stat
          label={t('admin.plan')}
          value={PLANS[currentPlan].name}
          hint={
            subscription?.periodEnd
              ? formatPhnomPenh(new Date(subscription.periodEnd), locale)
              : undefined
          }
        />
      </div>

      {/*
        §3: billing is per user. A merchant looking at this from inside one
        shop needs to know the balance is not that shop's — otherwise the first
        time credits drop without them doing anything, it reads as a bug.
      */}
      <p className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
        {t('admin.creditsShared')}
      </p>

      {(charges.data?.charges.length ?? 0) > 0 ? (
        <Panel title={t('admin.chargePending')}>
          {charges.data!.charges.map((charge) => (
            <div key={charge.id} className="flex flex-col items-center gap-2 py-2">
              <p className="text-sm font-semibold">
                {t('admin.payToUpgrade', {
                  amount: formatMoney({ amount: charge.chargeAmount, currency: charge.currency }),
                })}
              </p>
              {charge.qrPayload ? <QrCode payload={charge.qrPayload} size={220} /> : null}
              {/*
                The exact-amount warning is the one thing on this screen that
                must be read. A round-number transfer matches nothing and the
                merchant waits for an upgrade that will not come (§3).
              */}
              <p
                className="max-w-sm text-center text-xs"
                style={{ color: 'var(--color-ink-soft)' }}
              >
                {t('admin.payToUpgradeHint')}
              </p>
            </div>
          ))}
        </Panel>
      ) : null}

      <Panel title={t('admin.plan')}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {/*
            Free is in the grid, not hidden. A merchant on free should see
            exactly where they stand and what the next step costs — and a
            merchant who lapsed back to free should not have to guess why
            their premium features locked.
          */}
          {(['free', 'starter', 'pro', 'business'] as const).map((planId) => {
            const plan = PLANS[planId];
            const active = currentPlan === planId;
            return (
              <div
                key={planId}
                className="panel p-4"
                style={active ? { borderColor: 'var(--color-brand)', borderWidth: 2 } : undefined}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold">{plan.name}</span>
                  <span className="text-lg font-bold">
                    {/* "$0.00" reads like a billing error rather than an offer. */}
                    {plan.priceUsdCents === 0
                      ? t('admin.planFree')
                      : formatMoney({ amount: plan.priceUsdCents, currency: 'USD' })}
                  </span>
                </div>
                <p className="mt-1 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                  {plan.maxShops} {t('admin.shops')} · {plan.monthlyCredits}{' '}
                  {t('admin.credits')} · {plan.maxStaff} × {t('admin.team')} ·{' '}
                  {plan.maxProducts} {t('admin.products')}
                </p>
                <ul className="mt-3 flex flex-col gap-1 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                  {plan.features.slice(0, 6).map((feature) => (
                    <li key={feature}>• {feature.replace(/_/g, ' ')}</li>
                  ))}
                </ul>
                {active ? (
                  <Pill tone="brand">✓</Pill>
                ) : plan.priceUsdCents > 0 ? (
                  /*
                    This opens a CHARGE — it does not switch the plan. The plan
                    moves when the transfer lands (§3), which is why the button
                    says "pay", not "switch".
                  */
                  <Button
                    size="sm"
                    className="mt-3 w-full"
                    disabled={start.isPending}
                    onClick={() => planId !== 'free' && start.mutate(planId)}
                  >
                    {start.isPending && start.variables === planId ? (
                      <Loader2 className="animate-spin" />
                    ) : null}
                    {t('admin.upgrade')}
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs" style={{ color: 'var(--color-ink-faint)' }}>
          {/* §3: renewal is a KHQR from the platform bot, never a stored card. */}
          {t('notification.merchant.subscription_reminder', { plan: '', days: 3, amount: '' })}
        </p>
      </Panel>

      <Panel title={t('admin.topUp')}>
        <div className="grid gap-3 sm:grid-cols-3">
          {CREDIT_PACKS.map((pack) => (
            <div key={pack.id} className="panel flex items-center justify-between p-3">
              <span className="font-semibold">{pack.credits}</span>
              <span>{formatMoney({ amount: pack.priceUsdCents, currency: 'USD' })}</span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title={t('admin.credits')} padded={false}>
        {ledger.length === 0 ? (
          <Empty title={t('common.empty')} />
        ) : (
          <Rows>
            {ledger.map((entry) => (
              <Row key={entry.id}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{entry.note ?? entry.kind.replace(/_/g, ' ')}</p>
                  <p className="text-xs" style={{ color: 'var(--color-ink-faint)' }}>
                    {formatPhnomPenh(new Date(entry.createdAt), locale)}
                  </p>
                </div>
                <span
                  className="font-semibold tabular-nums"
                  style={{ color: entry.amount >= 0 ? 'var(--color-positive)' : 'var(--color-ink)' }}
                >
                  {entry.amount >= 0 ? '+' : ''}
                  {entry.amount}
                </span>
              </Row>
            ))}
          </Rows>
        )}
      </Panel>
    </div>
  );
}
